import { useEffect, useMemo, useRef, useState } from "react";
import {
  parseCommaList,
  workflowStatusLabel,
} from "@/components/compliance/case-filters.utils";
import {
  ComplianceCaseFilters,
  ComplianceCaseFiltersValue,
} from "@/components/compliance/case-filters";
import { formatTransactionVolume } from "@/components/compliance/admin-case-detail.utils";
import { CreateCaseDialog } from "@/components/compliance/create-case-dialog";
import { RefreshIconButton } from "@/components/refresh-icon-button";
import { RequirementsNeededTree } from "@/components/compliance/requirements-needed-tree";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { canAdminRead, canAdminWrite } from "@/lib/auth";
import { complianceApi } from "@/lib/compliance-api";
import { useAdminComplianceCasesList } from "@/hooks/useAdminComplianceCasesList";
import {
  complianceQueryKeys,
  getAdminCasesInfiniteQueryOptions,
  getAdminMerchantsQueryOptions,
} from "@/lib/compliance-query-keys";
import { cn } from "@/lib/utils";
import type { ComplianceCase, WorkflowStatus } from "@/types/compliance";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod/v4";

const searchParamsSchema = z.object({
  merchantId: z.string().optional(),
  applicationId: z.string().optional(),
  customerName: z.string().optional(),
  workflowStatuses: z.string().optional(),
});

export const Route = createFileRoute(
  "/(app)/_admin-layout/admin_/compliance/cases",
)({
  validateSearch: zodValidator(searchParamsSchema),
  loaderDeps: ({ search }) => ({
    merchantId: search.merchantId,
    applicationId: search.applicationId,
    customerName: search.customerName,
    workflowStatuses: search.workflowStatuses,
  }),
  loader: async ({
    context: { queryClient, accessToken, authorities },
    deps,
  }) => {
    if (!canAdminRead(authorities)) return;
    await queryClient.ensureQueryData(
      getAdminMerchantsQueryOptions({ accessToken }),
    );
    void queryClient.prefetchInfiniteQuery(
      getAdminCasesInfiniteQueryOptions({
        accessToken,
        limit: 50,
        merchantId: deps.merchantId,
        applicationId: deps.applicationId,
        customerName: deps.customerName,
        workflowStatuses: deps.workflowStatuses,
      }),
    );
  },
  component: AdminComplianceCases,
});

function getAdminStageBadgeClass(workflowStatus: WorkflowStatus | undefined) {
  if (!workflowStatus) return "bg-slate-100 text-slate-600";
  if (workflowStatus === "PENDING_VOLUME_REVIEW")
    return "bg-red-100 text-red-800 hover:bg-red-100";
  if (workflowStatus === "PARTIAL" || workflowStatus === "WAITING_FOR_INPUT")
    return "bg-amber-100 text-amber-800 hover:bg-amber-100";
  if (workflowStatus === "APPROVED")
    return "bg-emerald-100 text-emerald-800 hover:bg-emerald-100";
  return "bg-slate-100 text-slate-600";
}

function AdminComplianceCases() {
  const context = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const loadMoreRef = useRef<HTMLTableRowElement>(null);

  const hasWriteAccess = canAdminWrite(context.authorities);

  const [createCaseOpen, setCreateCaseOpen] = useState(false);

  const filters: ComplianceCaseFiltersValue = {
    workflowStatuses: search.workflowStatuses,
    merchantId: search.merchantId,
    applicationId: search.applicationId,
    customerName: search.customerName,
  };

  const merchantsQuery = useSuspenseQuery(
    getAdminMerchantsQueryOptions({ accessToken: context.accessToken }),
  );

  const merchantNameById = merchantsQuery.data
    ? Object.fromEntries(
        merchantsQuery.data.map((m) => [m.merchantId, m.companyName]),
      )
    : undefined;

  const merchantOptions = useMemo(() => {
    const baseOptions = merchantsQuery.data
      ? merchantsQuery.data.map((m) => ({
          value: m.merchantId,
          label: m.companyName,
        }))
      : [];

    if (!search.merchantId) {
      return [{ value: "", label: "All merchants" }, ...baseOptions];
    }

    const selectedExists = baseOptions.some(
      (option) => option.value === search.merchantId,
    );
    if (selectedExists) {
      return [{ value: "", label: "All merchants" }, ...baseOptions];
    }

    return [
      { value: "", label: "All merchants" },
      {
        value: search.merchantId,
        label:
          merchantsQuery.data.find((m) => m.merchantId === search.merchantId)
            ?.companyName ?? "",
        sublabel: search.merchantId,
      },
      ...baseOptions,
    ];
  }, [merchantsQuery.data, search.merchantId]);

  const casesQuery = useAdminComplianceCasesList({
    accessToken: context.accessToken,
    merchantId: search.merchantId,
    applicationId: search.applicationId,
    customerName: search.customerName,
    workflowStatuses: search.workflowStatuses,
  });

  const [previousWorkflowStatuses, setPreviousWorkflowStatuses] = useState<
    string[]
  >([]);
  useEffect(() => {
    if (!casesQuery.isFetching) {
      setPreviousWorkflowStatuses(
        parseCommaList(search.workflowStatuses ?? ""),
      );
    }
  }, [casesQuery.isFetching, search.workflowStatuses]);

  const cases = useMemo(() => {
    const allCases = casesQuery.data.pages.flatMap((page) => page.items);
    const deduped = Array.from(
      new Map(allCases.map((item) => [item.id, item])).values(),
    );

    const parseIso = (iso?: string) => {
      const ms = iso ? Date.parse(iso) : Number.NaN;
      return Number.isFinite(ms) ? ms : 0;
    };

    const getSortKey = (item: ComplianceCase) => {
      const ws = item.workflowStatus;
      if (ws === "PENDING_VOLUME_REVIEW") return 0;
      if (ws === "PARTIAL" || ws === "WAITING_FOR_INPUT") return 1;
      return 2;
    };

    return [...deduped].sort((a, b) => {
      const aKey = getSortKey(a);
      const bKey = getSortKey(b);
      if (aKey !== bKey) return aKey - bKey;
      return parseIso(a.updatedAt) - parseIso(b.updatedAt);
    });
  }, [casesQuery.data.pages]);

  const createCaseMutation = useMutation({
    mutationFn: (payload: {
      applicationId: string;
      merchantId: string;
      merchantName: string;
      customerFullName: string;
      requirementCodes: string[];
      transactionVolume?: string;
      transactionVolumeCurrency?: string;
      transactionCount?: number;
    }) =>
      complianceApi.admin.createCase({
        accessToken: context.accessToken,
        ...payload,
      }),
    onSuccess: () => {
      toast.success("Compliance case created");
      setCreateCaseOpen(false);
      void queryClient.invalidateQueries({
        queryKey: ["compliance", "admin", "cases"],
      });
      void queryClient.invalidateQueries({
        queryKey: complianceQueryKeys.adminOverview(),
      });
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to create compliance case",
      );
    },
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (
          first?.isIntersecting &&
          casesQuery.hasNextPage &&
          !casesQuery.isFetchingNextPage
        ) {
          void casesQuery.fetchNextPage();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
      },
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [
    casesQuery.fetchNextPage,
    casesQuery.hasNextPage,
    casesQuery.isFetchingNextPage,
  ]);

  return (
    <div className="space-y-3">
      <section className="rounded-2xl border border-volume-text-purple/20 bg-gradient-to-br from-volume-light-purple/15 to-slate-50 p-3">
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-volume-text-purple/70">
            Case Filters
          </p>
          <div className="flex items-center gap-2">
            <RefreshIconButton
              label="Refresh admin compliance cases"
              onRefresh={() => casesQuery.refetch()}
            />
            {hasWriteAccess ? (
              <Button
                size="sm"
                className="bg-volume-text-purple text-white shadow-md shadow-volume-text-purple/25 hover:bg-volume-text-purple/90"
                onClick={() => setCreateCaseOpen(true)}
              >
                <Plus className="mr-1 size-4" />
                Create Case
              </Button>
            ) : null}
          </div>
        </div>
        <ComplianceCaseFilters
          value={filters}
          showAdminFields
          merchantOptions={merchantOptions}
          isFetching={casesQuery.isFetching}
          previousWorkflowStatuses={previousWorkflowStatuses}
          onChange={(next) => {
            navigate({
              to: ".",
              search: {
                ...search,
                merchantId: next.merchantId || undefined,
                applicationId: next.applicationId || undefined,
                customerName: next.customerName || undefined,
                workflowStatuses: next.workflowStatuses,
              },
            });
          }}
        />
      </section>

      <CreateCaseDialog
        open={createCaseOpen}
        onOpenChange={setCreateCaseOpen}
        accessToken={context.accessToken}
        onSubmit={(data) => createCaseMutation.mutate(data)}
        isPending={createCaseMutation.isPending}
      />

      <div className="overflow-hidden rounded-xl border border-volume-text-purple/20 bg-white">
        <Table className="bg-white">
          <TableHeader className="bg-slate-50/70">
            <TableRow className="border-slate-200">
              <TableHead className="h-8 min-w-0 px-2 py-1.5 text-xs font-medium text-slate-600">
                Merchant
              </TableHead>
              <TableHead className="h-8 min-w-0 px-2 py-1.5 text-xs font-medium text-slate-600">
                Customer
              </TableHead>
              <TableHead className="h-8 min-w-0 px-2 py-1.5 text-xs font-medium text-slate-600">
                Transactions
              </TableHead>
              <TableHead className="h-8 min-w-0 px-2 py-1.5 text-xs font-medium text-slate-600">
                Requirements
              </TableHead>
              <TableHead className="h-8 min-w-0 px-2 py-1.5 text-xs font-medium text-slate-600">
                Status
              </TableHead>
              <TableHead className="sticky right-0 h-8 w-12 shrink-0 bg-slate-50/95 px-2 py-1.5 text-right text-xs font-medium text-slate-600 backdrop-blur-[1px]">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cases.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-5 text-center text-sm text-slate-600"
                >
                  No cases found for selected filters.
                </TableCell>
              </TableRow>
            ) : null}
            {cases.map((item) => {
              const needsReview =
                item.workflowStatus === "PENDING_VOLUME_REVIEW";
              const stageLabel = item.workflowStatus
                ? workflowStatusLabel[item.workflowStatus]
                : "—";
              return (
                <TableRow
                  key={item.caseId}
                  className={cn(
                    "group border-slate-200 hover:bg-volume-light-purple/10",
                    needsReview && "bg-red-50/40 hover:bg-red-50/60",
                  )}
                >
                  <TableCell className="min-w-[100px] max-w-[140px] truncate px-2 py-1.5">
                    <div className="truncate text-sm font-medium text-slate-900">
                      {(item.merchantId && merchantNameById
                        ? merchantNameById[item.merchantId]
                        : null) ?? (
                        <span className="text-slate-400 italic">
                          Unknown merchant
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="min-w-[80px] max-w-[120px] truncate px-2 py-1.5 text-sm text-slate-700">
                    {item.customerName.trim() ? (
                      item.customerName
                    ) : (
                      <span className="text-slate-400 italic">
                        Unknown customer
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap px-2 py-1.5 text-sm text-slate-700">
                    {item.transactionVolume != null ? (
                      <>
                        {formatTransactionVolume(item.transactionVolume)}{" "}
                        {item.transactionVolumeCurrency}
                      </>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                    {item.transactionCount != null ? (
                      <div className="flex flex-col text-slate-500">
                        <span>
                          {item.transactionCount} transaction
                          {item.transactionCount !== 1 ? "s" : ""}
                        </span>
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="min-w-0 max-w-[140px] px-2 py-1.5">
                    <RequirementsNeededTree
                      requirements={item.requirements}
                      noRequirementsLabel="None"
                    />
                  </TableCell>
                  <TableCell className="min-w-0 px-2 py-1.5">
                    {item.workflowStatus ? (
                      <Badge
                        className={cn(
                          "capitalize",
                          getAdminStageBadgeClass(item.workflowStatus),
                        )}
                      >
                        {stageLabel}
                      </Badge>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "sticky right-0 w-12 shrink-0 px-2 py-1.5 text-right shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.04)]",
                      needsReview
                        ? "bg-red-50/80 group-hover:bg-red-50/60"
                        : "bg-white group-hover:bg-volume-light-purple/10",
                    )}
                  >
                    <Link
                      to="/admin/compliance/cases/$caseId"
                      params={{ caseId: item.id }}
                      preload="intent"
                    >
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1 px-2 text-xs font-medium text-volume-text-purple hover:bg-volume-light-purple/40"
                      >
                        View
                        <ArrowRight className="size-3.5" />
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
            {casesQuery.hasNextPage || casesQuery.isFetchingNextPage ? (
              <TableRow ref={loadMoreRef}>
                <TableCell colSpan={6} className="py-8 text-center">
                  {casesQuery.isFetchingNextPage ? (
                    <div className="flex items-center justify-center gap-2">
                      <Spinner width={20} height={20} />
                      <span className="text-sm text-slate-600">
                        Loading more cases...
                      </span>
                    </div>
                  ) : (
                    <div className="h-20" />
                  )}
                </TableCell>
              </TableRow>
            ) : cases.length > 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-6 text-center">
                  <span className="text-sm text-slate-500">
                    No more cases to load
                  </span>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
